import React, { SyntheticEvent } from 'react';
import { Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { GroupSelect } from '~/components/GroupSelect';
import {
    flatGroupGetOptionPickedFromCompoundKey,
    FlatGroupOptionType,
} from '~/components/GroupSelect/FlatGroupOptionType';
import { Select } from '~/components/Select';
import { ClientContext, ServicesContext } from '~/services';
import { ReferralContext, ReferralContextType } from '~/services/ReferralContext';
import { emptyPagination, PaginationType } from '~/util';
import { mapProviderResults, ProviderType } from '~/util/providerUtils';
import {
    OpenLR,
    ProviderResults,
    ReferralInfo,
    usePlanCodesList,
    usePlanMarketList,
    useSpecialtiesList,
} from '.';

export interface ReferralSelectOption {
    label: string;
    value: number;
    marketId?: undefined | number;
    nuccs?: {
        id: number;
        code: string;
        // eslint-disable-next-line babel/camelcase
        display_name: string;
    }[];
}

const distanceOptions = [
    { value: 5, label: '5 miles' },
    { value: 10, label: '10 miles' },
    { value: 25, label: '25 miles' },
    { value: 50, label: '50 miles' },
    { value: 100, label: '100 miles' },
];

interface ReferralsProps {
    patient: PatientType;
    show: boolean;
}
export function Referrals({ patient, show }: ReferralsProps) {
    const services = React.useContext(ServicesContext);
    const { selectedProvider } = React.useContext(ReferralContext) as ReferralContextType;
    const { client, isPriorYearSelected } = React.useContext(ClientContext);
    const specialtyList = useSpecialtiesList();
    const marketList = usePlanMarketList();
    const planCodesList = usePlanCodesList();
    const [selectedMarket, setSelectedMarket] = React.useState<ReferralSelectOption>();
    const [selectedDistance, setSelectedDistance] = React.useState<{
        value: number;
        label: string;
    }>();
    const [searchInput, setSearchInput] = React.useState('');
    const [specialties, setSpecialties] = React.useState<FlatGroupOptionType[]>([]);
    const [planCode, setPlanCode] = React.useState<ReferralSelectOption>();
    const [inNetwork, setInNetwork] = React.useState(false);
    const [providerList, setProviderList] = React.useState<ProviderType[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [hasSearched, setHasSearched] = React.useState(false);
    const paginationRef = React.useRef<PaginationType>(emptyPagination());
    const defaultPlanEnabled = services.user.hasFeatureToggle('default_referral_plan');

    React.useEffect(() => {
        let defaultPlanSelected = false;
        // if toggle enabled, set default market with plan from client settings
        if (defaultPlanEnabled && client?.settings.core_app_all_patient_plan_id !== null) {
            marketList.some((market) => {
                if (market.value === client?.settings.core_app_all_patient_plan_id) {
                    setSelectedMarket(market);
                    defaultPlanSelected = true;
                    return true;
                }
                return false;
            });
        }
        // if no default plan, then attempt to select a market matching patient
        if (!defaultPlanSelected && patient?.markets && patient.markets.length === 1) {
            marketList.some((market) => {
                if (market.marketId === patient.markets[0].id) {
                    setSelectedMarket(market);
                    return true;
                }
                return false;
            });
        }
        if (patient?.insurance_plans && patient.insurance_plans.length === 1) {
            planCodesList.some((pc) => {
                if (pc.label === patient.insurance_plans[0].code) {
                    setPlanCode(pc);
                    setInNetwork(true);
                    return true;
                }
                return false;
            });
        }
    }, [
        marketList,
        planCodesList,
        patient.markets,
        patient.insurance_plans,
        client?.settings.core_app_all_patient_plan_id,
        defaultPlanEnabled,
    ]);

    async function search(e?: SyntheticEvent) {
        if (e) {
            e.preventDefault();
            // Manual search event, set page back to 1
            paginationRef.current.current = 1;
        }
        setLoading(true);

        const params = [
            `patient_id=${patient.id}`,
            `page=${paginationRef.current.current}`,
            `per_page=${paginationRef.current.items_per_page}`,
            `plan=${selectedMarket?.value}`,
        ];

        if (specialties && specialties.length > 0) {
            params.push(
                `nuccs=${specialties
                    .map((x: FlatGroupOptionType) =>
                        flatGroupGetOptionPickedFromCompoundKey(x.value)
                    )
                    .join(',')}`
            );
        }

        if (inNetwork && planCode) params.push(`hub_tag=${planCode?.value}`);
        if (searchInput.trim()) params.push(`q=${searchInput}`);
        if (selectedDistance) params.push(`radius=${selectedDistance.value}`);
        const providerResults = await services.api.call({
            method: 'get',
            url: `/core-api/networks?${params.join('&')}`,
        });

        setProviderList(mapProviderResults(providerResults.data.results));
        paginationRef.current.current = providerResults.data.pagination.page;
        paginationRef.current.total = providerResults.data.pagination.results;
        paginationRef.current.last = providerResults.data.pagination.pages;
        setHasSearched(true);
        setLoading(false);
    }

    async function updatePagination(pagination: PaginationType) {
        paginationRef.current = { ...pagination };
        await search();
    }

    if (!show) return null;

    if (isPriorYearSelected && services.user.hasFeatureToggle('disable_prior_year_referrals')) {
        return (
            <div className="text-center mt-4 mb-4">
                To refer this patient, use the patient’s current year record in Zephyr or open
                LeadingReach.
            </div>
        );
    }

    return (
        <div>
            {!services.user.getExternalAccount('Core App') && (
                <div className="permissions-alert permissions-alert-card">
                    Link your Zephyr account to LeadingReach to begin sending referrals. To request
                    or link an account with LeadingReach, <Link to="/user">click here</Link>.
                </div>
            )}
            {services.user.getExternalAccount('Core App') && (
                <div className="permissions-alert permissions-alert-card">
                    All providers must continue to follow the appropriate health plan
                    prior-authorization guidelines.
                </div>
            )}
            {selectedProvider !== null ? (
                <ReferralInfo
                    patient={patient}
                    planId={selectedMarket?.value}
                    planCode={patient?.insurance_plans?.[0]?.code}
                />
            ) : (
                <>
                    <form onSubmit={search}>
                        <div className="mb-3">
                            <div className="row align-items-center">
                                <div className="col-12 col-md-10">
                                    <div className="row mb-2">
                                        <div className="col-12">
                                            <input
                                                type="text"
                                                placeholder="Search Organization, Provider, or NPI"
                                                className="form-control"
                                                value={searchInput}
                                                onChange={(e) => setSearchInput(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-12 col-md-6 col-lg-3 mb-2">
                                            <Select
                                                label="market"
                                                placeholder="Select Market"
                                                options={marketList}
                                                isClearable
                                                value={selectedMarket}
                                                onChange={setSelectedMarket}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3 mb-2">
                                            <GroupSelect
                                                label="specialties"
                                                placeholder="Select Specialty"
                                                options={specialtyList}
                                                value={specialties}
                                                onChange={(x: FlatGroupOptionType[]) =>
                                                    setSpecialties(x)
                                                }
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3 mb-2">
                                            <Select
                                                label="distance"
                                                placeholder="Select Distance"
                                                options={distanceOptions}
                                                isClearable
                                                value={selectedDistance}
                                                onChange={setSelectedDistance}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3 mb-2 d-flex align-items-center">
                                            <label
                                                className="form-label align-baseline mb-0"
                                                title={
                                                    planCode
                                                        ? ''
                                                        : 'There are no in network plan codes available for this patient'
                                                }
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="align-middle"
                                                    disabled={!planCode}
                                                    checked={inNetwork}
                                                    onChange={(e) => setInNetwork(e.target.checked)}
                                                />
                                                <span className="ms-2">Show in network only</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-2 text-end text-md-center">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading || !selectedMarket}
                                    >
                                        {loading && (
                                            <Spinner
                                                animation="border"
                                                variant="light"
                                                size="sm"
                                                role="status"
                                                className="me-1"
                                            />
                                        )}
                                        Search
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                    <div className="mb-3 d-flex align-items-center">
                        <OpenLR />
                        {services.user.hasFeatureToggle('send_ref_feedback') && (
                            <a
                                href="https://survey.zohopublic.com/zs/clClEz"
                                target="_blank"
                                rel="noreferrer"
                                className="ms-3"
                            >
                                <i className="fa-solid fa-bullhorn me-1" />
                                Give Feedback
                            </a>
                        )}
                    </div>
                    <ProviderResults
                        hasSearched={hasSearched}
                        loading={loading}
                        providerList={providerList}
                        pagination={paginationRef.current}
                        updatePagination={updatePagination}
                        planCode={patient?.insurance_plans?.[0]?.code}
                    />
                </>
            )}
        </div>
    );
}
